import { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Download, FileSpreadsheet, FileText } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const classDetails = {
  STAR: { color: "bg-emerald-100 text-emerald-800", label: "Star — mantieni e promuovi" },
  WORKHORSE: { color: "bg-amber-100 text-amber-800", label: "Workhorse — valuta prezzo" },
  PUZZLE: { color: "bg-sky-100 text-sky-800", label: "Puzzle — aumenta visibilità" },
  DOG: { color: "bg-red-100 text-red-800", label: "Dog — valuta rimozione" },
};

const DEFAULT_CATS = ["colazione", "caffetteria", "brunch", "aperitivo", "cucina", "dolci", "vini", "cocktail"];
const emptyForm = { name: "", category: "caffetteria", sale_price: 0, vat: 10, description: "", recipe_id: "", sales_count: 0 };

export default function Menu() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — menu mutations require menu.write.
  const canWrite = can("menu.write");
  const CATS = lists?.menu_categories?.length ? lists.menu_categories : DEFAULT_CATS;
  const [list, setList] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("append");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef();

  const load = async () => {
    const [m, r] = await Promise.all([api.get("/menu"), api.get("/recipes")]);
    setList(m.data); setRecipes(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, category: CATS[0] || "caffetteria" }); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || "", category: m.category || CATS[0], sale_price: m.sale_price ?? 0,
      vat: m.vat ?? 10, description: m.description || "", recipe_id: m.recipe_id || "",
      sales_count: m.sales_count ?? 0,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      sale_price: Number(form.sale_price),
      vat: Number(form.vat),
      sales_count: Number(form.sales_count),
      recipe_id: form.recipe_id || null,
    };
    try {
      if (editing) await api.put(`/menu/${editing.id}`, payload);
      else await api.post("/menu", payload);
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      setOpen(false);
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Eliminare "${m.name}"?`)) return;
    try { await api.delete(`/menu/${m.id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const filtered = useMemo(() => list.filter((m) => {
    if (catFilter !== "all" && m.category !== catFilter) return false;
    if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [list, q, catFilter]);

  const downloadTemplate = () => {
    fetch(`${API_BASE}/menu/import-template`, { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "menu_template.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLang, setPdfLang] = useState("it");
  const [pdfQr, setPdfQr] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const url = `${API_BASE}/menu/export-pdf?lang=${pdfLang}&include_qr=${pdfQr}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Errore export");
      const b = await r.blob();
      const link = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = link;
      a.download = `menu-carasau-${pdfLang}.pdf`;
      a.click();
      URL.revokeObjectURL(link);
      toast.success("Menu PDF scaricato");
      setPdfOpen(false);
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setPdfLoading(false);
    }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Seleziona un file"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mode", importMode);
      const res = await fetch(`${API_BASE}/menu/bulk-import`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore import");
      setImportResult(data);
      toast.success(`${data.imported} prodotti importati${data.errors?.length ? ` (${data.errors.length} errori)` : ""}`);
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setImportOpen(false); setImportFile(null); setImportResult(null); setImportMode("append");
    if (importInputRef.current) importInputRef.current.value = "";
  };

  return (
    <div data-testid="menu-page">
      <PageHeader eyebrow="Vendita" title="Menu & margini"
        subtitle="Modifica prezzi, IVA e ricetta collegata: food cost e classificazione (menu engineering) si aggiornano al volo."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setPdfOpen(true)} data-testid="export-pdf-btn">
              <FileText size={14} strokeWidth={1.5} /> Export PDF
            </Button>
            {canWrite && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-menu-btn">
              <Upload size={14} strokeWidth={1.5} /> Importa CSV/Excel
            </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              {canWrite && (
              <DialogTrigger asChild>
                <Button data-testid="new-menu-btn" onClick={openNew} className="bg-neutral-900 text-[#FDFBF7]">
                  <Plus size={14} strokeWidth={1.5} /> Nuovo prodotto menu
                </Button>
              </DialogTrigger>
              )}
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="font-serif">{editing ? "Modifica prodotto menu" : "Nuovo prodotto menu"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Nome</Label><Input data-testid="menu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Categoria</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="menu-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        {form.category && !CATS.includes(form.category) && <SelectItem value={form.category}>{form.category}</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Ricetta (opzionale)</Label>
                    <Select value={form.recipe_id || "none"} onValueChange={(v) => setForm({ ...form, recipe_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="menu-recipe"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuna —</SelectItem>
                        {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Prezzo di vendita (€)</Label><Input data-testid="menu-price" required type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></div>
                  <div><Label>IVA (%)</Label><Input data-testid="menu-vat" type="number" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
                  <div><Label>Vendite mese (indicativo)</Label><Input type="number" value={form.sales_count} onChange={(e) => setForm({ ...form, sales_count: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <DialogFooter className="col-span-2">
                    <Button data-testid="menu-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">{editing ? "Salva modifiche" : "Crea"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        } />

      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="menu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto…" className="max-w-xs" />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="menu-cat-filter" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[720px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Prodotto</th><th className="p-3">Categoria</th>
            <th className="p-3 text-right">Prezzo</th><th className="p-3 text-right">Food cost</th>
            <th className="p-3 text-right">Margine</th><th className="p-3 text-right">Vendite</th>
            <th className="p-3">Classe</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {filtered.map((m) => {
              const cd = classDetails[m.classification] || {};
              return (
                <tr key={m.id} className="border-t border-[#E2D9C8]" data-testid={`menu-${m.id}`}>
                  <td className="p-3 font-medium">
                    {m.name}
                    {m.description && <div className="text-xs text-neutral-500 font-normal">{m.description}</div>}
                  </td>
                  <td className="p-3 text-neutral-500 capitalize">{m.category}</td>
                  <td className="p-3 text-right">{fmtEUR(m.sale_price)}</td>
                  <td className="p-3 text-right text-neutral-500">{fmtEUR(m.food_cost)}</td>
                  <td className="p-3 text-right font-medium">{m.margin_pct}%</td>
                  <td className="p-3 text-right">{m.sales_count}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${cd.color}`}>{m.classification}</span>
                    <div className="text-xs text-neutral-500 mt-1">{cd.label}</div>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canWrite && (
                      <>
                        <button onClick={() => openEdit(m)} data-testid={`edit-menu-${m.id}`}
                          className="text-neutral-500 hover:text-neutral-900 mr-2"><Pencil size={14} strokeWidth={1.5} /></button>
                        <button onClick={() => del(m)} data-testid={`del-menu-${m.id}`}
                          className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-neutral-500">Nessun prodotto con questi filtri.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Export PDF dialog */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><FileText size={16} strokeWidth={1.5} /> Menu stampabile PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-neutral-600 border-l-2 border-[#D7C4A3] pl-3">
              Genera un PDF elegante in stile mediterraneo — perfetto da lasciare sui tavoli o inviare via email agli hotel partner.
            </div>
            <div>
              <Label>Lingua</Label>
              <Select value={pdfLang} onValueChange={setPdfLang}>
                <SelectTrigger data-testid="pdf-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={pdfQr} onChange={(e) => setPdfQr(e.target.checked)} data-testid="pdf-qr" />
              Includi QR code in fondo (link al menu online)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)}>Annulla</Button>
            <Button onClick={downloadPdf} disabled={pdfLoading} data-testid="pdf-download"
              className="bg-neutral-900 text-[#FDFBF7]">
              <Download size={14} strokeWidth={1.5} /> {pdfLoading ? "Genero…" : "Scarica PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => (v ? setImportOpen(true) : closeImport())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><FileSpreadsheet size={16} strokeWidth={1.5} /> Importa menu da CSV / Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-neutral-600 border-l-2 border-[#D7C4A3] pl-3">
              Colonne accettate (IT o EN): <b>name</b>, <b>category</b>, <b>sale_price</b>, <b>vat</b>, description, sales_count, recipe_id. Separatori CSV: <code>,</code> o <code>;</code>.
            </div>
            <button type="button" onClick={downloadTemplate} data-testid="download-template"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-[#E2D9C8] rounded hover:border-neutral-900">
              <Download size={12} strokeWidth={1.5} /> Scarica template CSV
            </button>
            <div>
              <Label>File (CSV o XLSX)</Label>
              <Input data-testid="import-file" ref={importInputRef} type="file" accept=".csv,.xlsx,.xlsm"
                onChange={(e) => { setImportFile(e.target.files?.[0]); setImportResult(null); }} />
              {importFile && <div className="text-xs text-neutral-500 mt-1">{importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            <div>
              <Label>Modalità</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Aggiungi ai prodotti esistenti</SelectItem>
                  <SelectItem value="replace">Sostituisci tutto il menu (elimina i vecchi)</SelectItem>
                </SelectContent>
              </Select>
              {importMode === "replace" && (
                <div className="text-xs text-red-700 mt-1">⚠ Tutti i prodotti attuali verranno eliminati prima dell'import.</div>
              )}
            </div>
            {importResult && (
              <div className={`border rounded-lg p-3 text-sm ${importResult.errors?.length ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="import-result">
                <div className="font-medium">
                  {importResult.imported} prodotti importati su {importResult.total_rows} righe
                  {importResult.replaced > 0 && ` · ${importResult.replaced} eliminati`}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs">
                    <div className="font-medium text-amber-900 mb-1">Errori ({importResult.errors.length}):</div>
                    <ul className="max-h-40 overflow-y-auto space-y-0.5">
                      {importResult.errors.slice(0, 20).map((err, i) => (
                        <li key={i}>Riga {err.row}{err.name ? ` (${err.name})` : ""}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>{importResult ? "Chiudi" : "Annulla"}</Button>
            {!importResult && (
              <Button onClick={runImport} disabled={!importFile || importing}
                data-testid="import-submit" className="bg-neutral-900 text-[#FDFBF7]">
                <Upload size={14} strokeWidth={1.5} /> {importing ? "Importazione…" : "Importa"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
